/**
 * S3 Upload Service
 * Handles file uploads to AWS S3 for branding assets (logos, favicons).
 *
 * TODO: This is a placeholder implementation. In production, you would:
 * 1. Get pre-signed S3 URLs from backend API
 * 2. Upload files directly to S3 using pre-signed URLs
 * 3. Return the final S3 URL for storage in branding configuration
 *
 * Example production flow:
 * 1. Client calls backend: POST /api/v1/branding/get-upload-url with file metadata
 * 2. Backend generates pre-signed S3 URL with appropriate permissions
 * 3. Client uploads file directly to S3 using pre-signed URL
 * 4. Client receives final S3 URL and saves it in branding configuration
 */

import api from './api';

/**
 * Normalize MIME types to match backend expectations.
 * Fixes Content-Type mismatches between browser and server.
 *
 * @param {string} mimeType - The MIME type from the browser
 * @returns {string} - Normalized MIME type
 */
const normalizeMimeType = (mimeType) => {
  const mimeMap = {
    'image/jpg': 'image/jpeg',  // Normalize jpg to jpeg (common browser variation)
    'image/x-icon': 'image/vnd.microsoft.icon', // ICO normalization
  };
  return mimeMap[mimeType] || mimeType;
};

/**
 * Upload a file to S3 for branding assets.
 *
 * @param {File} file - The file to upload
 * @param {string} assetType - Type of asset: 'logo', 'logo_dark', or 'favicon'
 * @param {string} tenantSlug - Tenant slug for S3 path organization
 * @returns {Promise<string>} - The S3 URL of the uploaded file
 */
export const uploadBrandingAsset = async (file, assetType, tenantSlug) => {
  try {
    // Step 1: Client-side validation (UX only, backend validates too)
    validateFile(file, assetType);

    // Step 2: Normalize MIME type to ensure consistency with backend
    const normalizedContentType = normalizeMimeType(file.type);

    // Step 3: Get pre-signed URL from backend
    const uploadUrlResponse = await api.post('/v1/branding/get-upload-url', {
      asset_type: assetType,
      filename: file.name,
      content_type: normalizedContentType,  // Use normalized MIME type
      file_size: file.size,
    });

    const { upload_url, s3_key, file_url } = uploadUrlResponse.data;

    // Step 4: Upload directly to S3 using pre-signed URL with same normalized Content-Type
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': normalizedContentType,  // CRITICAL: Must match pre-signed URL signature
        // x-amz-acl removed - S3 bucket has ACLs disabled ("Bucket owner enforced")
        // Public access controlled via bucket policy instead
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      // Log detailed error information for debugging
      const errorText = await uploadResponse.text();
      console.error('S3 Upload Failed:', {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
        url: upload_url.split('?')[0], // Log URL without query params (security)
        originalContentType: file.type,
        normalizedContentType: normalizedContentType,
        fileSize: file.size,
        fileName: file.name,
        errorBody: errorText,
      });
      throw new Error(`Failed to upload to S3: ${uploadResponse.status} ${errorText}`);
    }

    // Step 5: Verify upload with backend
    const verifyResponse = await api.post('/v1/branding/verify-upload', {
      s3_key: s3_key,
      asset_type: assetType,
    });

    if (!verifyResponse.data.success) {
      throw new Error('File verification failed');
    }

    // Step 6: Return verified file URL
    return verifyResponse.data.file_url;

  } catch (error) {
    console.error('S3 upload error:', error);
    throw new Error(`Failed to upload ${assetType}: ${error.message}`);
  }
};

/**
 * Validate file before upload.
 *
 * @param {File} file - The file to validate
 * @param {string} assetType - Type of asset
 * @throws {Error} - If file is invalid
 */
const validateFile = (file, assetType) => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Validate file type (SVG removed for security)
  const validTypes = {
    logo: ['image/png', 'image/jpeg'],
    logo_dark: ['image/png', 'image/jpeg'],
    favicon: ['image/x-icon', 'image/png'],
  };

  const allowedTypes = validTypes[assetType] || validTypes.logo;

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      `Invalid file type for ${assetType}. Allowed: ${allowedTypes.map(t => t.split('/')[1]).join(', ')}`
    );
  }

  // Validate file size
  const maxSizes = {
    logo: 2 * 1024 * 1024,        // 2MB
    logo_dark: 2 * 1024 * 1024,   // 2MB
    favicon: 1 * 1024 * 1024,     // 1MB
  };

  const maxSize = maxSizes[assetType] || maxSizes.logo;

  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    throw new Error(`File too large. Maximum size for ${assetType} is ${maxSizeMB}MB`);
  }

  // Validate file name
  if (file.name.length > 255) {
    throw new Error('File name too long (max 255 characters)');
  }

  // Check for potentially dangerous file names
  const dangerousPatterns = ['../', '..\\', '<script', 'javascript:'];
  const fileName = file.name.toLowerCase();

  for (const pattern of dangerousPatterns) {
    if (fileName.includes(pattern)) {
      throw new Error('Invalid file name');
    }
  }
};

/**
 * Delete a branding asset from S3.
 *
 * @param {string} s3Url - The S3 URL to delete
 * @returns {Promise<void>}
 */
export const deleteBrandingAsset = async (s3Url) => {
  try {
    // TODO: Implement backend endpoint: DELETE /api/v1/branding/delete-asset
    // await api.delete('/v1/branding/delete-asset', {
    //   data: { s3_url: s3Url }
    // });

    // PLACEHOLDER: Simulate deletion
    await new Promise(resolve => setTimeout(resolve, 500));

  } catch (error) {
    console.error('S3 delete error:', error);
    throw new Error(`Failed to delete asset: ${error.message}`);
  }
};

/**
 * Get file preview URL (for displaying uploaded images).
 * For S3 URLs, this is typically the URL itself.
 * For local files, this creates an Object URL.
 *
 * @param {File|string} fileOrUrl - File object or URL string
 * @returns {string} - Preview URL
 */
export const getFilePreviewUrl = (fileOrUrl) => {
  if (typeof fileOrUrl === 'string') {
    // Already a URL
    return fileOrUrl;
  }

  if (fileOrUrl instanceof File) {
    // Create object URL for preview
    return URL.createObjectURL(fileOrUrl);
  }

  return '';
};

/**
 * Revoke object URL to free memory.
 * Call this when component unmounts or file changes.
 *
 * @param {string} url - Object URL to revoke
 */
export const revokePreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

export default {
  uploadBrandingAsset,
  deleteBrandingAsset,
  getFilePreviewUrl,
  revokePreviewUrl,
};
